/**
 * Conversational learning platform APIs (gateway /v1/tutor/communication/*).
 */
import { createHttpClient } from '../../api/http';
import { serviceUrls } from '../../config/serviceUrls';

const TUTOR_BASE = import.meta.env.VITE_AI_TUTOR_BASE_URL || serviceUrls.gateway;
const client = createHttpClient(TUTOR_BASE);
const PREFIX = TUTOR_BASE.includes('ai-tutor') ? '/tutor' : '/v1/tutor';

export type EvaluateResponsePayload = {
  ok: boolean;
  matched_can_dos: { id: string; statement_en: string; statement_ta: string; confidence: number }[];
  unmet_can_dos: { id: string; statement_en: string; statement_ta: string }[];
  suggestions: string[];
  rubric_scores?: Record<string, { level: string; score: number }>;
  overall_level?: string;
  flags?: { estimated?: boolean; feedback_estimated?: boolean };
  mastery_labels?: { id: string; label: string }[];
  sentence_corrections?: {
    original_fragment: string;
    suggested_fragment: string;
    reason_en?: string;
    reason_ta?: string;
    severity: string;
  }[];
  transcript_highlights?: { start: number; end: number; kind: string }[];
  encouragement?: { ta: string; en: string };
  next_prompt?: { prompt_ta: string; prompt_en?: string };
};

export async function postEvaluateResponse(payload: {
  grade: number;
  lesson_no?: number;
  topic_id?: string;
  text?: string;
  transcript?: string;
  session_id?: string | null;
  context?: string | null;
}): Promise<EvaluateResponsePayload> {
  const r = await client.post<EvaluateResponsePayload>(`${PREFIX}/communication/evaluate-response`, payload);
  return r.data;
}

export async function postNextPrompt(payload: {
  grade: number;
  topic_title?: string;
  turn_index?: number;
  overall_level?: string;
  language_assist?: boolean;
}): Promise<{ ok: boolean; prompt_ta: string; prompt_en?: string }> {
  const r = await client.post(`${PREFIX}/communication/next-prompt`, payload);
  return r.data as { ok: boolean; prompt_ta: string; prompt_en?: string };
}

export async function getCommunicationBadgesMe(): Promise<{ ok: boolean; items: { badge_key?: string; earned_at?: string }[] }> {
  const r = await client.get(`${PREFIX}/communication/badges/me`);
  return r.data;
}

export async function getCommunicationSummary(userId?: string | null): Promise<Record<string, unknown>> {
  const r = await client.get(`${PREFIX}/reports/communication-summary`, {
    params: userId ? { user_id: userId } : {},
  });
  return r.data as Record<string, unknown>;
}

export async function listTeacherCommunicationAlerts(): Promise<{ ok: boolean; items: unknown[] }> {
  const r = await client.get(`${PREFIX}/communication/alerts`);
  return r.data as { ok: boolean; items: unknown[] };
}

export async function dismissCommunicationAlert(alertId: string): Promise<void> {
  await client.patch(`${PREFIX}/communication/alerts/${encodeURIComponent(alertId)}/dismiss`);
}

export async function createCommunicationAssignment(payload: Record<string, unknown>): Promise<unknown> {
  const r = await client.post(`${PREFIX}/communication/assignments`, payload);
  return r.data;
}

export async function listTeacherCommunicationAssignments(): Promise<{ ok: boolean; items: unknown[] }> {
  const r = await client.get(`${PREFIX}/communication/assignments`);
  return r.data as { ok: boolean; items: unknown[] };
}

export async function listMyCommunicationAssignments(): Promise<{ ok: boolean; items: unknown[] }> {
  const r = await client.get(`${PREFIX}/communication/assignments/mine`);
  return r.data as { ok: boolean; items: unknown[] };
}

export async function patchCommunicationAssignmentProgress(
  assignmentId: string,
  payload: { status: string; attempts_delta?: number }
): Promise<void> {
  await client.patch(`${PREFIX}/communication/assignments/${encodeURIComponent(assignmentId)}/progress`, payload);
}
